// HTML rendering extension: renders code blocks with lang="html" as live sandboxed previews
import { unsafeCSS } from "lit";
import { DebouncedFunc, debounce } from "lodash";
import { Plugin, PluginKey } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";

import {
    buildHtmlSrcdoc,
    createHeightMessageHandler,
    getDocumentCSSVars,
} from "@/lib/html-preview";
import { BaseExtension } from "@/wc/text-editor/lib/core/base-extension";

import styles from "./styles.scss?inline";

const pluginKey = new PluginKey("html");

const MAX_HEIGHT_FRACTION = 0.65;

const debouncedRenderFunctions = new Map<
    string,
    DebouncedFunc<typeof renderHtmlPreview>
>();
const cleanupHandlers = new Map<string, () => void>();

function renderHtmlPreview(
    source: string,
    container: HTMLElement,
    key: string
) {
    cleanupHandlers.get(key)?.();
    cleanupHandlers.delete(key);

    const iframe = document.createElement("iframe");
    // allow-same-origin is required for external CDN scripts (e.g. Tailwind) to
    // load correctly — null-origin sandboxes break CORS and CSP meta-tag inheritance.
    // Risk accepted: this is an authenticated internal tool with trusted authors.
    iframe.setAttribute("sandbox", "allow-scripts allow-same-origin");
    iframe.className = "html-preview-iframe";
    iframe.srcdoc = buildHtmlSrcdoc(source, getDocumentCSSVars());

    const cleanup = createHeightMessageHandler(iframe, (height) => {
        const maxHeight = window.innerHeight * MAX_HEIGHT_FRACTION;
        iframe.style.height = `${Math.min(height, maxHeight)}px`;
    });
    cleanupHandlers.set(key, cleanup);

    // Build header (title + toggle) – reconstructed each render so the toggle
    // listener is always fresh and reflects the current source-visible state.
    const expanded = container.classList.contains("source-visible");
    const toggleBtn = document.createElement("button");
    toggleBtn.className = "source-toggle-btn";
    toggleBtn.dataset.expanded = String(expanded);
    const chevron = document.createElement("span");
    chevron.className = "source-toggle-chevron";
    const toggleLabel = document.createElement("span");
    toggleLabel.className = "source-toggle-label";
    toggleLabel.textContent = expanded ? "Hide source" : "Show source";
    toggleBtn.appendChild(chevron);
    toggleBtn.appendChild(toggleLabel);
    toggleBtn.addEventListener("click", () => {
        const isExpanded = container.classList.toggle("source-visible");
        toggleBtn.dataset.expanded = String(isExpanded);
        toggleLabel.textContent = isExpanded ? "Hide source" : "Show source";
    });

    const footer = document.createElement("div");
    footer.className = "widget-footer";
    const titleSpan = document.createElement("span");
    titleSpan.className = "html-preview-text";
    titleSpan.textContent = "HTML preview";
    footer.appendChild(titleSpan);
    footer.appendChild(toggleBtn);

    container.innerHTML = "";
    container.appendChild(iframe);
    container.appendChild(footer);
}

export class HtmlExtension extends BaseExtension {
    plugins = () => {
        const htmlPlugin = new Plugin({
            key: pluginKey,
            state: {
                init: () => ({
                    decorations: DecorationSet.empty,
                    renderedBlocks: new Map<
                        string,
                        { container: HTMLElement; source: string }
                    >(),
                }),
                apply: (tr, pluginState) => {
                    if (!tr.docChanged) return pluginState;

                    const { renderedBlocks } = pluginState;
                    const decos: Decoration[] = [];
                    let count = 0;

                    tr.doc.descendants((node, pos) => {
                        if (
                            node.type.name === "code_block" &&
                            node.attrs.params === "html"
                        ) {
                            count++;
                            const key = `html-${count}`;

                            let entry = renderedBlocks.get(key);
                            const isNew = !entry;
                            if (!entry) {
                                const container = document.createElement("div");
                                container.className = "html-widget";
                                entry = { container, source: "" };
                                renderedBlocks.set(key, entry);
                            }

                            if (isNew || node.textContent !== entry.source) {
                                entry.source = node.textContent;

                                let debouncedRender =
                                    debouncedRenderFunctions.get(key);
                                if (!debouncedRender) {
                                    debouncedRender = debounce(
                                        renderHtmlPreview,
                                        300
                                    );
                                    debouncedRenderFunctions.set(
                                        key,
                                        debouncedRender
                                    );
                                }

                                debouncedRender(
                                    node.textContent,
                                    entry.container,
                                    key
                                );
                            }

                            decos.push(
                                Decoration.widget(pos, () => entry!.container, {
                                    key,
                                    side: -1,
                                })
                            );
                        }
                    });

                    for (const [key] of renderedBlocks) {
                        if (!decos.some((d) => d.spec.key === key)) {
                            renderedBlocks.delete(key);

                            const debouncedRender =
                                debouncedRenderFunctions.get(key);
                            if (debouncedRender) {
                                debouncedRender.cancel();
                                debouncedRenderFunctions.delete(key);
                            }

                            cleanupHandlers.get(key)?.();
                            cleanupHandlers.delete(key);
                        }
                    }

                    return {
                        decorations: DecorationSet.create(tr.doc, decos),
                        renderedBlocks,
                    };
                },
            },
            props: {
                decorations(state) {
                    return this.getState(state)?.decorations;
                },
            },
            view(editorView) {
                // Remove source-visible from all containers when entering reading-mode
                // so that returning to edit mode always starts with source hidden.
                const observer = new MutationObserver(() => {
                    if (!editorView.dom.classList.contains("reading-mode"))
                        return;
                    const state = pluginKey.getState(editorView.state);
                    if (!state) return;
                    for (const { container } of state.renderedBlocks.values()) {
                        container.classList.remove("source-visible");
                        const btn =
                            container.querySelector<HTMLButtonElement>(
                                ".source-toggle-btn"
                            );
                        if (!btn) continue;
                        btn.dataset.expanded = "false";
                        const label = btn.querySelector<HTMLSpanElement>(
                            ".source-toggle-label"
                        );
                        if (label) label.textContent = "Show source";
                    }
                });
                observer.observe(editorView.dom, {
                    attributes: true,
                    attributeFilter: ["class"],
                });
                return { destroy: () => observer.disconnect() };
            },
        });

        return [htmlPlugin];
    };

    css = [unsafeCSS(styles)];
}
